import type { FC, MouseEvent, KeyboardEvent } from "react";
import { Link } from "next-view-transitions";
import { useSetAtom } from "jotai";
import { format } from "date-fns";
import { clsx } from "clsx";
import {
	faEdit,
	faEllipsisV,
	faTrashAlt,
} from "@fortawesome/pro-solid-svg-icons";

import type { QuizPopulated } from "@vita-learning/types";

import { editAtom } from "~/client/services";
import { Skeleton } from "~/shared/components";
import { formatSourceOutput } from "~/shared/services";
import { bem as _bem } from "~/shared/styles";
import { getErrorMessage } from "~/shared/utils";

import { CancelButton, DeleteButton } from "../../buttons";
import { Dropdown } from "../../lists";
import { Alert, modalAtom, useAlert, usePopupQueue } from "../../portals";
import { useQuizzesStore } from "../../stores";
import { Icon } from "../../widgets";

import "./styles.scss";

export interface QuizItemProps {
	readonly quiz: QuizPopulated;
	readonly onDelete?: (quiz: QuizPopulated) => Promise<void>;
	readonly onEdit?: () => Promise<void>;
	readonly dateFormat?: string;
	readonly className?: string;
}

const bem = _bem("quiz-item");

export const QuizItem: FC<QuizItemProps> = ({
	className,
	quiz,
	onDelete,
	onEdit,
}) => {
	const setEditId = useSetAtom(editAtom);
	const setModal = useSetAtom(modalAtom);
	const { enqueueSuccess, enqueueError } = usePopupQueue();
	const { onCancel, onOpen, onClose } = useAlert();

	const editQuiz = useQuizzesStore(s => s.edit);
	const deleteQuiz = useQuizzesStore(s => s.remove);

	const handleClick = (
		e: KeyboardEvent<HTMLSpanElement> | MouseEvent<HTMLSpanElement>
	) => {
		e.preventDefault();
		e.nativeEvent.stopImmediatePropagation();

		setEditId(quiz.id);
		editQuiz(quiz.id);
	};

	const handleDelete = async () => {
		try {
			const deletedQuiz = await deleteQuiz(quiz.id);
			onDelete?.(deletedQuiz);

			enqueueSuccess(`${deletedQuiz.title} has been deleted`, "Quiz Deleted");
			onClose();
		} catch (e) {
			enqueueError(getErrorMessage(e), "Error Deleting Quiz");
		}
	};

	const dropdownItems = [
		{
			label: "Edit",
			icon: faEdit,
			onClick: () => {
				onEdit?.();
				setModal("EDIT_QUIZ");
			},
		},
		{
			label: "Delete",
			icon: faTrashAlt,
			onClick: onOpen(quiz.id),
		},
	];

	return (
		<>
			<Link
				className={clsx(bem("link"), className)}
				href={`/quizzes/${quiz.id}/practice`}
				aria-label="Practice quiz"
			>
				<div className={bem()}>
					<div
						className={clsx(
							bem("divider"),
							quiz.isCompleted && bem(undefined, "striked")
						)}
					>
						<h4 className={bem("title")}>{quiz.title}</h4>
					</div>
					<div
						className={clsx(
							bem("divider"),
							quiz.isCompleted && bem(undefined, "striked")
						)}
					>
						<p>{quiz.date && format(quiz.date, "eee, MMM do")}</p>
						<p>
							{quiz.source === "auto" ? null : formatSourceOutput(quiz.source)}
						</p>
					</div>
					<div className={clsx(bem("divider"), bem("divider", "actions"))}>
						{/** biome-ignore lint/a11y/noStaticElementInteractions: Stop propagation */}
						<span
							className={bem("dropdown")}
							onClick={handleClick}
							onKeyUp={handleClick}
						>
							<Dropdown items={dropdownItems}>
								<Icon icon={faEllipsisV} />
							</Dropdown>
						</span>
					</div>
				</div>
			</Link>
			<Alert
				id={quiz.id}
				title="Are you sure you want to delete this quiz?"
				message="This action cannot be undone."
			>
				<DeleteButton onClick={handleDelete}>Delete</DeleteButton>
				<CancelButton onClick={onCancel}>Cancel</CancelButton>
			</Alert>
		</>
	);
};

export const QuizItemFallback = () => (
	<div className={bem()}>
		<div className={bem("divider")}>
			<h4 className={bem("title")}>
				<Skeleton />
			</h4>
		</div>
		<div className={bem("divider")}>
			<p>
				<Skeleton />
			</p>
			<p>
				<Skeleton />
			</p>
		</div>
		<div className={clsx(bem("divider"), bem("divider", "actions"))}>
			<Dropdown items={[]}>
				<Icon icon={faEllipsisV} />
			</Dropdown>
		</div>
	</div>
);
